import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { User } from '../types';
import { calculateAge, convertToDisplayFormat, convertToIsoFormat, validateDateFormat } from '../utils/ageCalculator';
import QRCode from 'qrcode';
import ParentalDataManagement from './ParentalDataManagement';
import AvatarDisplay from './AvatarDisplay';

const StudentProfile: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [refreshedUser, setRefreshedUser] = useState<User | null>(currentUser);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(30);
  const [sessionsPerWeek, setSessionsPerWeek] = useState(5);
  const [savingSetup, setSavingSetup] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showParentalDataManagement, setShowParentalDataManagement] = useState(false);

  // Refresh user data from Firestore
  const refreshUserData = async () => {
    if (!currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const updatedUser = {
          uid: currentUser.uid,
          ...userData
        } as User;
        setRefreshedUser(updatedUser);
        
        // Check if user needs to complete setup
        if (!updatedUser.hasCompletedSetup) {
          setShowSetupModal(true);
        }
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  // Save practice preferences
  const savePracticePreferences = async () => {
    if (!currentUser) return;
    
    setSavingSetup(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        practiceSessionDuration: sessionDuration,
        practiceSessionsPerWeek: sessionsPerWeek,
        hasCompletedSetup: true
      });
      
      // Update local state
      setRefreshedUser(prev => prev ? {
        ...prev,
        practiceSessionDuration: sessionDuration,
        practiceSessionsPerWeek: sessionsPerWeek,
        hasCompletedSetup: true
      } : null);
      
      setShowSetupModal(false);
    } catch (error) {
      console.error('Error saving practice preferences:', error);
    } finally {
      setSavingSetup(false);
    }
  };

  // Update birthdate
  const updateBirthdate = async (newBirthdate: string) => {
    if (!currentUser) return;
    
    // Validate DD-MM-YYYY format
    if (!validateDateFormat(newBirthdate)) {
      alert('Please enter a valid birthdate in DD-MM-YYYY format (e.g., 15-03-2010)');
      return;
    }
    
    try {
      // Convert DD-MM-YYYY to YYYY-MM-DD for storage
      const isoFormatDate = convertToIsoFormat(newBirthdate);
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        birthdate: isoFormatDate
      });
      
      // Update local state
      setRefreshedUser(prev => prev ? {
        ...prev,
        birthdate: isoFormatDate
      } : null);
      
      // Refresh user data to ensure consistency
      refreshUserData();
    } catch (error) {
      console.error('Error updating birthdate:', error);
      alert('Failed to update birthdate. Please try again.');
    }
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (currentUser.role !== 'student') {
      navigate('/dashboard');
      return;
    }

    // Set initial user data from currentUser
    setRefreshedUser(currentUser);
    
    // Then try to refresh from Firestore
    refreshUserData();
    
    // Set loading to false since we don't auto-generate QR code anymore
    setLoading(false);
  }, [currentUser, navigate]);

  const generateQRCode = async () => {
    if (!refreshedUser) return;

    try {
      setLoading(true);
      const qrData = {
        type: 'student-link',
        studentId: refreshedUser.uid,
        studentName: refreshedUser.name,
        timestamp: Date.now()
      };

      const qrString = JSON.stringify(qrData);
      const dataUrl = await QRCode.toDataURL(qrString, {
        width: 256,
        margin: 2,
        color: {
          dark: '#1f2937',
          light: '#ffffff'
        }
      });
      
      setQrCodeDataUrl(dataUrl);
      setShowQRCode(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleQRCode = () => {
    setShowQRCode(!showQRCode);
  };

  // Request goal change or update directly
  const requestGoalChange = async (field: 'practiceSessionDuration' | 'practiceSessionsPerWeek', newValue: number) => {
    if (!refreshedUser) return;

    try {
      // Check if student has linked teachers
      const hasLinkedTeachers = refreshedUser.linkedTeachers && refreshedUser.linkedTeachers.length > 0;

      if (hasLinkedTeachers) {
        // Request approval from teacher
        const goalChangeRequest = {
          [field]: newValue,
          requestedAt: new Date(),
          status: 'pending' as const
        };

        await updateDoc(doc(db, 'users', refreshedUser.uid), {
          pendingGoalChanges: goalChangeRequest
        });
        
        alert('Goal change request sent to your teacher for approval.');
      } else {
        // Update directly since no teacher is linked
        await updateDoc(doc(db, 'users', refreshedUser.uid), {
          [field]: newValue,
          pendingGoalChanges: null // Clear any existing pending changes
        });
        
        alert('Practice goal updated successfully!');
      }

      // Refresh user data
      await refreshUserData();
    } catch (error) {
      console.error('Error updating goal:', error);
      alert('Failed to update goal. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const copyStudentId = () => {
    if (refreshedUser?.uid) {
      navigator.clipboard.writeText(refreshedUser.uid);
      // You could add a toast notification here
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-600">Generating QR code...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <nav className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-0">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center">
                <div className="text-3xl mr-3">👤</div>
                <h1 className="text-xl font-bold text-white">
                  Student Profile
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/profile/edit')}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Edit Profile
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0 space-y-8">
          
      {/* Profile Overview Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Profile</h2>
        
        {/* Goal Approval Notice */}
        {refreshedUser?.pendingGoalChanges?.status === 'pending' && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Goal Approval Pending</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>Your practice goals are waiting for teacher approval. Once approved by your linked teacher, these goals will become your official targets.</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* No Linked Teacher Notice */}
        {refreshedUser?.role === 'student' && (!refreshedUser?.linkedTeachers || refreshedUser.linkedTeachers.length === 0) && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Independent Practice</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>You can freely edit your practice goals since you don't have a linked teacher. Changes will be applied immediately when you click "Edit".</p>
                </div>
              </div>
            </div>
          </div>
        )}
            
            {/* Avatar and Basic Info */}
            <div className="flex items-center space-x-6 mb-8">
              <AvatarDisplay 
                user={refreshedUser || {}}
                size="xl"
                alt="Profile"
              />
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{refreshedUser?.name}</h3>
                <p className="text-gray-600">{refreshedUser?.nickname}</p>
                <p className="text-sm text-gray-500">{refreshedUser?.instrument}</p>
              </div>
            </div>

            {/* Profile Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Age</label>
                <p className="mt-1 text-sm text-gray-900">
                  {refreshedUser?.birthdate ? calculateAge(refreshedUser.birthdate) : 'Not set'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Birth Date</label>
                <div className="mt-1 flex items-center space-x-3">
                  <p className="text-sm text-gray-900">
                    {refreshedUser?.birthdate ? new Date(refreshedUser.birthdate).toLocaleDateString('en-AU', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'Not set'}
                  </p>
                  <button
                    onClick={() => {
                      const currentDisplayDate = refreshedUser?.birthdate ? convertToDisplayFormat(refreshedUser.birthdate) : '';
                      const newBirthdate = prompt('Enter your birth date (DD-MM-YYYY):', currentDisplayDate);
                      if (newBirthdate && newBirthdate !== currentDisplayDate) {
                        updateBirthdate(newBirthdate);
                      }
                    }}
                    className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
                  >
                    Edit
                  </button>
                </div>
              </div>
              
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Plan Status</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                  refreshedUser?.planStatus === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {refreshedUser?.planStatus}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Daily Practice Goal</label>
                <div className="mt-1 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-gray-900">
                      {refreshedUser?.pendingGoalChanges?.practiceSessionDuration ? 
                        `${refreshedUser.pendingGoalChanges.practiceSessionDuration} minutes (requested)` :
                        refreshedUser?.practiceSessionDuration ? 
                          `${refreshedUser.practiceSessionDuration} minutes` : 
                          'Not set'
                      }
                    </p>
                    {refreshedUser?.pendingGoalChanges?.practiceSessionDuration && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        refreshedUser.pendingGoalChanges.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800'
                          : refreshedUser.pendingGoalChanges.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {refreshedUser.pendingGoalChanges.status === 'pending' && '⏳ Pending'}
                        {refreshedUser.pendingGoalChanges.status === 'approved' && '✅ Approved'}
                        {refreshedUser.pendingGoalChanges.status === 'rejected' && '❌ Rejected'}
                      </span>
                    )}
                  </div>
                  {!refreshedUser?.pendingGoalChanges?.practiceSessionDuration && (
                    <button
                      onClick={() => {
                        const currentGoal = refreshedUser?.practiceSessionDuration || 30;
                        const newGoal = prompt('Enter new daily practice goal (minutes):', currentGoal.toString());
                        if (newGoal && parseInt(newGoal) !== currentGoal && parseInt(newGoal) > 0) {
                          requestGoalChange('practiceSessionDuration', parseInt(newGoal));
                        }
                      }}
                      className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
                    >
{refreshedUser?.linkedTeachers && refreshedUser.linkedTeachers.length > 0 ? 'Request Change' : 'Edit'}
                    </button>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Practice Frequency</label>
                <div className="mt-1 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-gray-900">
                      {refreshedUser?.pendingGoalChanges?.practiceSessionsPerWeek ? 
                        `${refreshedUser.pendingGoalChanges.practiceSessionsPerWeek} times per week (requested)` :
                        refreshedUser?.practiceSessionsPerWeek ? 
                          `${refreshedUser.practiceSessionsPerWeek} times per week` : 
                          'Not set'
                      }
                    </p>
                    {refreshedUser?.pendingGoalChanges?.practiceSessionsPerWeek && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        refreshedUser.pendingGoalChanges.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800'
                          : refreshedUser.pendingGoalChanges.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {refreshedUser.pendingGoalChanges.status === 'pending' && '⏳ Pending'}
                        {refreshedUser.pendingGoalChanges.status === 'approved' && '✅ Approved'}
                        {refreshedUser.pendingGoalChanges.status === 'rejected' && '❌ Rejected'}
                      </span>
                    )}
                  </div>
                  {!refreshedUser?.pendingGoalChanges?.practiceSessionsPerWeek && (
                    <button
                      onClick={() => {
                        const currentFreq = refreshedUser?.practiceSessionsPerWeek || 3;
                        const newFreq = prompt('Enter new practice frequency (sessions per week):', currentFreq.toString());
                        if (newFreq && parseInt(newFreq) !== currentFreq && parseInt(newFreq) > 0) {
                          requestGoalChange('practiceSessionsPerWeek', parseInt(newFreq));
                        }
                      }}
                      className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
                    >
{refreshedUser?.linkedTeachers && refreshedUser.linkedTeachers.length > 0 ? 'Request Change' : 'Edit'}
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Week Start Day</label>
                <p className="mt-1 text-sm text-gray-900">
                  {refreshedUser?.weekStartDay || 'Sunday'}
                </p>
              </div>
            </div>
          </div>

          {/* Link with Teacher Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Link with Your Teacher</h2>
            
            <div className="text-center mb-6">
              <p className="text-gray-600 mb-4">
                Share your QR code with your teacher to connect your accounts and track your progress together.
              </p>
              
              <button
                onClick={qrCodeDataUrl ? toggleQRCode : generateQRCode}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 inline-flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                <span>{showQRCode ? 'Hide QR Code' : (qrCodeDataUrl ? 'Show QR Code' : 'Generate QR Code')}</span>
              </button>
            </div>

            {qrCodeDataUrl && showQRCode && (
              <div className="text-center">
                <div className="inline-block p-6 bg-white border-2 border-gray-200 rounded-lg shadow-sm">
                  <img 
                    src={qrCodeDataUrl} 
                    alt="Student QR Code" 
                    className="w-64 h-64 mx-auto"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  Have your teacher scan this QR code to link your accounts
                </p>
              </div>
            )}

            {/* Student ID Alternative */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Alternative: Manual Linking</h4>
              <p className="text-sm text-gray-600 mb-3">
                If QR code scanning isn't possible, you can share your Student ID with your teacher:
              </p>
              <div className="flex items-center space-x-3">
                <p className="text-sm text-gray-900 font-mono bg-white px-3 py-2 rounded border flex-1 truncate">
                  {refreshedUser?.uid}
                </p>
                <button
                  onClick={copyStudentId}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors duration-200"
                >
                  Copy ID
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Email your Student ID to your teacher and they can link accounts manually.
              </p>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 mb-2">How to link with your teacher:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Click "Show QR Code" above, or copy your Student ID</li>
                <li>Show the QR code to your teacher, or email them your Student ID</li>
                <li>Your teacher will scan the QR code or manually enter your Student ID</li>
                <li>You'll be automatically linked in their dashboard</li>
                <li>Your teacher can then view your lessons and practice sessions</li>
              </ol>
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> You can only be linked to one teacher at a time. If you need to switch teachers, 
                  your current teacher will need to unlink you first.
                </p>
              </div>
            </div>

            {/* Parental Data Management Section */}
            {refreshedUser?.parentalConsent && (
              <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-purple-900 mb-1">Parental Data Management</h4>
                    <p className="text-sm text-purple-700">
                      Parents can manage and delete account data for users under 13.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowParentalDataManagement(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
                  >
                    <span>👨‍👩‍👧‍👦</span>
                    <span>Manage Data</span>
                  </button>
                </div>
                <div className="mt-3 text-xs text-purple-600">
                  <p>This account was created with parental consent. Parents can view, manage, and delete all data associated with this account.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Practice Setup Modal */}
      {showSetupModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-indigo-100 rounded-full">
                <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                </svg>
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-lg font-medium text-gray-900">Set Up Your Practice Preferences</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Let's personalize your practice experience by setting your preferred session duration and frequency.
                  </p>
                </div>
                
                <div className="space-y-4 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      How long should each practice session be?
                    </label>
                    <select
                      value={sessionDuration.toString()}
                      onChange={(e) => setSessionDuration(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="1">1 minute (Testing)</option>
                      <option value="15">15 minutes</option>
                      <option value="20">20 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">60 minutes</option>
                      <option value="90">90 minutes</option>
                      <option value="120">120 minutes</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      How many times per week do you want to practice?
                    </label>
                    <select
                      value={sessionsPerWeek}
                      onChange={(e) => setSessionsPerWeek(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value={3}>3 times per week</option>
                      <option value={4}>4 times per week</option>
                      <option value={5}>5 times per week</option>
                      <option value={6}>6 times per week</option>
                      <option value={7}>Every day</option>
                    </select>
                  </div>
                </div>
                
                <div className="mt-6 flex justify-center space-x-3">
                  <button
                    onClick={savePracticePreferences}
                    disabled={savingSetup}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    {savingSetup ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Parental Data Management Modal */}
      {showParentalDataManagement && refreshedUser && (
        <ParentalDataManagement
          childUserId={refreshedUser.uid}
          childName={refreshedUser.name || refreshedUser.nickname || 'Child'}
          onClose={() => setShowParentalDataManagement(false)}
        />
      )}
    </div>
  );
};

export default StudentProfile;
